import styled from "styled-components";
import dayjs from "dayjs";

import {MAIN_THEME} from "styles/colors";

import { Expense } from "interfaces/expenses";
import { Icon } from "components/atoms";
import {IconName} from "interfaces/icons";
import FileUploadService from "services/storage/fileUpload";

const TableCell = styled.div`
	color: ${MAIN_THEME.TEXT_SECONDARY};
	display: flex;
	align-items: center;
	font-size: 14px;
	justify-content: flex-start;
	text-align: left;
	min-height: 52px;
	padding: 10px 8px;
	background: transparent;
	border: none;
	border-bottom: 1px solid ${MAIN_THEME.BORDER_COLOR};
	transition: background 0.15s ease;
	white-space: normal;
	overflow-wrap: anywhere;
	word-break: break-word;

	@media (max-width: 768px) {
		font-size: 13px;
		min-height: 44px;
		padding: 8px 6px;
	}
`;

interface TableProps {
	columns: string;
}

const Table = styled.div<TableProps>`
	display: grid;
	grid-template-columns: ${(props) => props.columns};
	gap: 0;

	&:hover {
		${TableCell} {
			background-color: ${MAIN_THEME.TABLE_HOVER};
		}
	}
`;

const LinkButton = styled.button`
	border: none;
	background: none;
	cursor: pointer;
	padding: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${MAIN_THEME.ACCEPT_COLOR};

	&:hover {
		opacity: 0.7;
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;

interface Props {
	data: Expense;
	showProperty?: boolean;
	visibleColumnIds?: string[];
	gridTemplateColumns?: string;
}

const ExpenseTableItem = (props: Props): JSX.Element => {
		const { data, showProperty, visibleColumnIds, gridTemplateColumns } = props;

		const { name, description, amount, expense_date, currency, receiptUrl } =
			data;


	const handleViewReceipt = async () => {
		if (!receiptUrl) return;

		try {
			const signedUrl = await FileUploadService.getSignedUrl(receiptUrl);
			window.open(signedUrl, "_blank");
		} catch (error) {
			console.error("Failed to get receipt URL:", error);
		}
	};

	const renderReceiptButton = () => {
		if (receiptUrl) {
			return (
				<TableCell>
					<LinkButton onClick={handleViewReceipt} title="View receipt">
						<Icon
							name={IconName.EYE}
							size={16}
							color={MAIN_THEME.ACCEPT_COLOR}
						/>
					</LinkButton>
				</TableCell>
			);
		}

		return <TableCell>-</TableCell>;
	};

	const defaultGridColumns = showProperty
		? "1fr 1fr 1.3fr 0.9fr 0.8fr 0.8fr 0.5fr"
		: "1fr 1.3fr 0.9fr 0.8fr 0.8fr 0.5fr";

	const isVisible = (id: string) => {
		if (!visibleColumnIds) return true;
		return visibleColumnIds.includes(id);
	}

	return (
		<Table columns={gridTemplateColumns || defaultGridColumns}>
			{showProperty && isVisible('property') ? <TableCell>{data.propertyName || ""}</TableCell> : null}
			{isVisible('name') && <TableCell>{name || "-"}</TableCell>}
			{isVisible('description') && <TableCell>{description}</TableCell>}
			{isVisible('amount') && <TableCell>{amount}</TableCell>}
			{isVisible('currency') && <TableCell>{currency}</TableCell>}
			{isVisible('date') && <TableCell>{dayjs(expense_date).format("D MMM, YYYY")}</TableCell>}
			{isVisible('receipt') && renderReceiptButton()}
		</Table>
	);
};

export default ExpenseTableItem;
